import { UploadedFile } from './common';
import { Discussion } from './discussion';
import { Idea } from './idea';
import { MetricUnitType } from './idea-metrics';
import { User } from './user';

export interface A3PageInfoType {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor?: string;
  endCursor?: string;
}

export const A3Roles = {
  A3_SPONSOR: 'A3_SPONSOR',
  A3_OWNER: 'A3_OWNER',
  A3_COACH: 'A3_COACH',
  A3_MEMBER: 'A3_MEMBER',
} as const;

export const A3Statuses = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  DROPPED: 'DROPPED',
  DEPLOYED: 'DEPLOYED',
  COMPLETED: 'COMPLETED',
} as const;

export type A3Role = typeof A3Roles[keyof typeof A3Roles];

export type A3Status = typeof A3Statuses[keyof typeof A3Statuses];

export const A3BadgeNames = {
  Novice: 'Novice',
  Apprentice: 'Apprentice',
  Pro: 'Pro',
  Master: 'Master',
} as const;

export const A3_BADGE_PATH = {
  NOVICE: '/badges/novice.svg',
  APPRENTICE: '/badges/apprentice.svg',
  PRO: '/badges/pro.svg',
  MASTER: '/badges/master.svg',
};

export type A3BadgeType = typeof A3BadgeNames[keyof typeof A3BadgeNames];

export const A3BadgeStatuses = {
  LOCKED: 'LOCKED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
} as const;

export type A3BadgeStatus =
  typeof A3BadgeStatuses[keyof typeof A3BadgeStatuses];

export interface A3SearchParams {
  first?: number;
  after?: string;
  filter?: A3SearchParamsFilter;
}

export interface A3SearchParamsFilter {
  id?: number;
  title?: string;
  userIds?: number[];
  modelEnabled?: boolean;
  startDate?: string;
  endDate?: string;
  impactedLocationIds?: string[];
  processUniverseLevelIds?: string[];
  statuses?: A3Status[];
  a3Roles?: A3Role[];
  metricIds?: string[];
  tagIds?: string[];
}

export interface A3MySpaceSearchParams {
  page?: number;
  limit?: number;
  filter?: {
    id?: number;
    title?: string;
    a3Roles?: A3Role;
    userId?: number;
  };
}

export interface A3Badge {
  id: number;
  name: string;
  description: string;
  orderNumber: number;
}

export interface A3BadgeProgress {
  total: number;
  progress: number;
  coached: number;
  practitioner: boolean;
  foundation: boolean;
}

export interface ProcessUniverseLevel {
  id: string;
  name: string;
  grandParent?: string;
  parentName?: string;
  subParent?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface A3 {
  id?: string;
  title?: string;
  status: A3Status;
  problemStatement?: string;
  backgroundDescription?: string;
  currentDesign?: string;
  currentDesignFailures?: string;
  currentDesignRoot?: string;
  targetDesign?: string;
  targetDesignActions?: string;
  targetDesignGuidance?: string;
  buildExecutePlan?: string;
  buildLessonsLearnt?: string;
  buildNextSteps?: string;
  buildResults?: string;
  pmLinks?: string[];
  attachments?: Array<{
    id: string;
    attachment: UploadedFile;
  }>;
  idea?: Idea;
  startDate?: string;
  endDate?: string;
  publishedAt?: string;
  createdBy?: User;
  createdAt?: string;
  updatedAt?: string;
  jsonConfig?: string;
  preview?: UploadedFile;
  tags?: Array<{
    id: string;
    a3Id: string;
    tag: {
      id: string;
      name: string;
    };
    createdBy?: User;
    createdAt?: string;
  }>;
  impactedLocations?: Array<{
    id: string;
    a3Id: string;
    impactedLocation: {
      id: string;
      name: string;
      createdAt?: string;
      updatedAt?: string;
    };
    createdBy?: User;
    createdAt?: string;
  }>;
  processUniverseLevels?: Array<{
    id: string;
    a3Id: string;
    processUniverseLevel?: ProcessUniverseLevel;
    createdBy?: User;
    createdAt?: string;
  }>;
  topics?: Array<{
    id: string;
    a3Id: string;
    topic: {
      id: string;
      name: string;
    };
    createdBy?: User;
    createdAt?: string;
  }>;
  metrics?: Array<{
    id: string;
    a3Id: string;
    metric: {
      id: string;
      name: string;
      unit?: {
        id: string;
        label: string;
        type: string;
      };
    };
    before: string;
    after: string;
    metricType: MetricUnitType;
    createdAt?: string;
  }>;
  discussion?: Discussion;
  collaborators?: Array<{
    id: string;
    user: User;
    permission: {
      id: string;
      name: string;
      type: string;
      resource?: {
        id: string;
        name: string;
      };
    };
    resourceId?: string;
    roles?: Array<{
      id: string;
      name: string;
    }>;
  }>;
  rating?: A3RatingResponse[];
  ratingQuestions?: A3RatingQuestion[];
  modelEnabled?: boolean;
  aiAssisted?: boolean;
}

export interface A3RatingResponse {
  id: string;
  a3Id: number;
  questionId: string;
  answer?: boolean;
  createdBy?: User;
}

export interface A3RatingQuestion {
  id: string;
  questionText: string;
  weight: string;
  orderNumber: number;
}

export interface ImpactedLocation {
  id: string;
  name: string;
  parentName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface A3UserStatistics {
  owned: number;
  sponsored: number;
  coached: number;
  member: number;
}

export interface A3UserStatistics {
  colleaguesEngaged: number;
  a3Submission: number;
  owner: number;
  sponsor: number;
  coach: number;
  trainingCompletion: number;
}

export interface A3StatisticsChart {
  months: string;
  colleaguesEngaged: number;
  a3Submission: number;
  owner: number;
  sponsor: number;
  coach: number;
}

export interface A3CountGroupedDto {
  group: string;
  count: number;
}

export interface A3MetricsDetails {
  id: string;
  metric: string;
  before: number;
  after: number;
  a3: number;
  unit: string;
}

export interface A3DashboardFilter {
  processUniverseLevelIds?: string[];
  impactedLocationIds?: string[];
  a3Statuses?: string[];
  a3TagIds?: string[];
  startDate?: string;
  endDate?: string;
  a3Roles?: A3Role;
  userIds?: number[];
}

export interface A3RatingStatistics {
  a3Rated: number;
  averageRating: number;
  a3ByRatings: A3RatingByRating[];
}

export interface A3RatingByRating {
  rating: number;
  a3Count: number;
  percentage: number;
}

export interface A3MDStatistics {
  submissions: number;
  averageAging: number;
  averageRating: number;
  mdEngaged: number;
}

export interface A3AnalyzeCriteria {
  id: string;
  criteria: string;
  slug: string;
  orderNum: number;
}

export interface A3AnalyzeResult {
  criteriaMet: boolean;
  criteriaSlug: string;
  reasoning: string;
}

export interface A3AnalyzeResponse {
  id: string;
  responseLog: string;
  result: A3AnalyzeResult[];
  statusCode: number;
}
